import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { ActivatedRoute, ParamMap, PRIMARY_OUTLET, Router, UrlTree } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import type {
  ColDef,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
  ValueFormatterParams,
  ICellRendererParams,
} from 'ag-grid-community';
import { CallsService, callsQueryKey, CallFilterField } from './calls.service';
import { CALL_LOAD_ERROR_TEXT, CALL_NOT_AVAILABLE_TEXT, CallLookupService } from './call-lookup.service';
import {
  ALL_24_FIELD_NAMES,
  CallInformation,
  callStatusLabel,
  callStatusTone,
  DEFAULT_VISIBLE_FIELDS,
  TOGGLEABLE_FIELDS,
  TOGGLEABLE_FIELD_LABELS,
} from './call-information.model';
import { formatDuration } from '../../shared/utils/format-duration';
import { escapeHtml } from '../../shared/utils/escape-html';
import { toLocalIsoDay } from '../../shared/utils/iso-date.util';
import { MatDialog } from '@angular/material/dialog';
import { CallDetailsDialogComponent } from './call-details/call-details-dialog.component';
import { CallDetailsTab } from './call-details/call-details.model';
import {
  LargeRangeConfirmDialogComponent,
  LargeRangeConfirmData,
  LargeRangeConfirmResult,
} from './large-range-confirm-dialog.component';
import { IdAutocompleteComponent } from '../../shared/id-autocomplete/id-autocomplete.component';
import { employeeToHit } from '../../shared/id-autocomplete/employee-to-hit.util';
import { SearchEntityFilter, SearchResult } from '../search/search.model';
import { SearchService } from '../search/search.service';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { EmployeeApiService } from '../../core/services/employee-api.service';
import { LoggingService } from '../../core/services/logging.service';
import { distinctUntilChanged, filter, map, Observable, Subscription } from 'rxjs';
import {
  HLM_MENU,
  HlmAlertComponent,
  HlmBtnDirective,
  HlmCheckboxComponent,
  HlmIconComponent,
  HlmInputDirective,
  HlmTooltipDirective,
  ToastService,
} from '../../ui';
import { copyToClipboard } from '../../shared/utils/clipboard';
import { CALL_VIEWER_PATH } from '../../shared/utils/call-viewer-link.util';

/** True when `url` (primary outlet) is the Call Viewer route itself. */
function isCallViewerUrl(url: UrlTree): boolean {
  const segments = url.root.children[PRIMARY_OUTLET]?.segments ?? [];
  return `/${segments.map((segment) => segment.path).join('/')}` === CALL_VIEWER_PATH;
}

/**
 * Berlin timestamp formatter — compact form for the dense calls table.
 *   "30.04.26 06:28:56" — 2-digit year saves ~3 chars per row vs 4-digit, and the
 *   call viewer is always operating on recent calls so the century is unambiguous.
 *   Seconds stay because they're useful for support / Graylog correlation.
 * Used by timestamp, firstLoadBegin, processEndTime.
 */
const BERLIN_FMT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});


/** Berlin HH:mm — for the compact "loaded 14:32" freshness stamp on the result toolbar. */
const BERLIN_TIME_FMT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const MS_PER_DAY = 86400000;
/** Days threshold above which a search is considered "large" — drives both the inline warn + the modal confirm. */
const WARN_RANGE_DAYS = 31;
/** sessionStorage key used to remember the "don't ask again" choice for the active tab. */
const LARGE_RANGE_OPT_OUT_KEY = 'cmt-calls-large-range-ack';

/**
 * Form the filter bar manages. All fields are optional — empty values are never sent to the backend.
 * Phone Service ID is intentionally NOT in the UI form — old CMT and Kundenverwaltung never exposed it.
 * Dates are stored in `YYYY-MM-DDTHH:mm` local format as produced by <input type="datetime-local">.
 */
interface FilterFormShape {
  customerId: FormControl<string>;
  userAccountId: FormControl<string>;
  agentId: FormControl<string>;
  /** Single-day mode: one date (`YYYY-MM-DD`). Empty when range mode is active. */
  singleDate: FormControl<string>;
  /** Range mode: full local datetime (`YYYY-MM-DDTHH:mm`). Empty when single-day mode. */
  start: FormControl<string>;
  end: FormControl<string>;
}

/** Snapshot of filters that produced the currently-loaded result set — drives active-filter chips. */
interface ActiveFilters {
  customerId?: string;
  /** Human-readable name for the chip (e.g. "Schlaue Software GmbH") — only when a hit was picked. */
  customerLabel?: string;
  userAccountId?: string;
  userAccountLabel?: string;
  phoneServiceId?: string;
  phoneServiceLabel?: string;
  agentId?: string;
  agentLabel?: string;
  start?: string;
  end?: string;
  rangePresetLabel?: string;
}

/** Preset chip keys — drive both the active highlight and the date-mode (single vs range). */
type TimePresetKey = 'today' | '1h' | '7d' | '30d' | 'custom';
type DateMode = 'single' | 'range';

interface TimePreset {
  key: TimePresetKey;
  label: string;
  mode: DateMode;
}

/** Format a Date as `YYYY-MM-DDTHH:mm` in local time — for `<input type="datetime-local">`. */
function toLocalIsoMinute(d: Date): string {
  const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const MS_PER_HOUR = 60 * 60 * 1000;

function endAfterStart(ctrl: AbstractControl): ValidationErrors | null {
  const s = ctrl.get('start')?.value;
  const e = ctrl.get('end')?.value;
  if (!s || !e) return null;
  return new Date(e) <= new Date(s) ? { endBeforeStart: true } : null;
}

/** Default value formatter: swap null/undefined/empty → em-dash. */
function emDashIfNil(value: unknown): string {
  if (value == null || value === '') return '—';
  return String(value);
}

function formatBerlin(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return BERLIN_FMT.format(d).replace(', ', ' ');
}

/**
 * Answered cell — small chip mirroring the Status pill language so the column
 * carries the same visual weight at scan-speed. Yes uses `success` (green dot,
 * tinted bg), No uses muted (grey dot, neutral bg). Null shows em-dash.
 */
function acceptedCellRenderer(params: ICellRendererParams<CallInformation>): string {
  if (params.value == null) return '<span class="text-muted-foreground">—</span>';
  if (params.value) {
    return (
      `<span class="inline-flex items-center rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success" aria-label="Call answered: yes">` +
        `Yes` +
      `</span>`
    );
  }
  return (
    `<span class="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground" aria-label="Call answered: no">` +
      `No` +
    `</span>`
  );
}

/**
 * Type-chip palette — distinct tones per call type so the column reads at a glance
 * instead of all chips looking identical. Mapping uses Spartan token classes so
 * each theme stays harmonious. Unknown values fall back to a muted chip.
 */
const TYPE_TONE: Record<string, string> = {
  ANRUF:       'bg-primary/15 text-primary',                // standard inbound — primary blue/teal
  TESTAUFRUF:  'bg-warning/15 text-warning',                // test call — amber
  TESTANRUF:   'bg-warning/15 text-warning',                // test call (variant)
  RUECKRUF:    'bg-success/15 text-success',                // callback — green
  WEITERLEITUNG: 'bg-accent/25 text-accent-foreground',     // forwarded — amber accent
  AUSGEHEND:   'bg-ring-purple/15 text-ring-purple',        // outbound — purple
  OUTBOUND:    'bg-ring-purple/15 text-ring-purple',
};

function badgeCellRenderer(params: ICellRendererParams<CallInformation>): string {
  if (params.value == null || params.value === '') {
    return '<span class="text-muted-foreground">—</span>';
  }
  const text = String(params.value);
  const tone = TYPE_TONE[text.toUpperCase()] ?? 'bg-muted text-muted-foreground';
  return `<span class="inline-flex items-center rounded-full ${tone} px-2 py-0.5 text-xs font-medium">${text}</span>`;
}

/**
 * Status badge renderer — colored dot + label, matching the row-tint legend.
 *   BUSY     → destructive (red)
 *   RING     → ring-purple
 *   NOTIZ    → primary (blue) — call has an attached callnote
 *   TESTAUFRUF / TESTANRUF → warning (amber) — not-a-real-call diagnostic
 *   other    → muted (grey)
 * The dot is the strongest visual signal at column scan-distance; pill background
 * reinforces it and stays readable against the row tint.
 */
function statusCellRenderer(params: ICellRendererParams<CallInformation>): string {
  if (params.value == null || params.value === '') {
    return '<span class="text-muted-foreground">—</span>';
  }
  const raw = String(params.value);
  // List view keeps the raw API enum as the visible text — operators reference
  // these tokens in Graylog / support tickets. The human-readable translation
  // surfaces on hover via the native `title` tooltip.
  const tone = callStatusTone(raw);
  const label = callStatusLabel(raw);
  const tooltip = label && label !== raw ? `${label} (${raw})` : raw;
  return (
    `<span class="inline-flex items-center rounded-full ${tone.pillClass} px-2 py-0.5 text-xs font-medium" title="${escapeHtml(tooltip)}">` +
      `${escapeHtml(raw)}` +
    `</span>`
  );
}

/** Duration column value formatter — uses the shared formatDuration util. */
function durationFormatter(params: ValueFormatterParams): string {
  return formatDuration(params.value);
}

/**
 * EB-3813: status code identifying transferred calls. For these rows the
 * backend's `callDurationInSeconds` ("Talk") covers the entire CDR span from
 * CALL → END_CALL — including the time after transfer when the caller spoke
 * to the mediated party, not to the agent. We surface that with a small
 * "+xfer" affordance + tooltip on the Talk cell.
 */
// Exported for unit tests — kept module-local in spirit (no other consumers).
export const TRANSFERRED_CALL_STATUS = 'vermittelt';

/**
 * EB-3813: Talk cell renderer. Renders the formatted duration; for transferred
 * calls (`status === 'vermittelt'`) appends a muted "+xfer" tag with a tooltip
 * pointing operators at the Process column for the agent's actual talk time.
 */
export function talkCellRenderer(params: ICellRendererParams<CallInformation>): string {
  const formatted = formatDuration(params.value);
  const isTransferred = (params.data?.status ?? '') === TRANSFERRED_CALL_STATUS;
  if (!isTransferred) {
    return escapeHtml(formatted);
  }
  // Discreet glyph (↗) marks the cell without crowding the number. The full
  // explanation lives in the native title tooltip — operators who care about
  // the distinction discover it on hover; everyone else just reads the time.
  return (
    `<span class="inline-flex items-baseline gap-1" ` +
      `title="Includes time after transfer (caller ↔ mediated party). For the agent talk time see the Agent Time column.">` +
      `<span>${escapeHtml(formatted)}</span>` +
      `<span class="text-muted-foreground text-[0.7rem] leading-none" aria-label="transferred call">↗</span>` +
    `</span>`
  );
}

/** Timestamp column value formatter — Berlin locale (dd.MM.yyyy HH:mm:ss). */
function berlinFormatter(params: ValueFormatterParams): string {
  return formatBerlin(params.value);
}

/** Default column value formatter — em-dash for null/undefined. */
function nullAsDashFormatter(params: ValueFormatterParams): string {
  return emDashIfNil(params.value);
}

// escapeHtml moved to shared/utils/escape-html.ts (imported at top of file).

/**
 * CCHSID cell renderer — compact form for the calls table:
 *   [ AB12CD34… ⧉ ]
 * Shows the first 8 hex chars + ellipsis to fit a narrow column. The full ID is
 * available via the native `title` tooltip (works inside scroll containers — see
 * CLAUDE.md pitfall #10) and via the copy button. Detail dialog shows the full ID.
 */
function cchsidCellRenderer(params: ICellRendererParams<CallInformation>): string {
  if (params.value == null || params.value === '') {
    return '<span class="text-muted-foreground">—</span>';
  }
  const v = String(params.value);
  const safe = escapeHtml(v);
  const short = v.length > 9 ? escapeHtml(v.slice(0, 8)) + '…' : safe;
  return `<span class="inline-flex w-full items-center gap-1.5" title="${safe}">` +
    `<span class="font-mono-feat text-xs">${short}</span>` +
    `<button type="button" ` +
    `class="cmt-copy-cchsid inline-flex h-5 w-5 flex-none items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" ` +
    `data-cchsid="${safe}" ` +
    `aria-label="Copy CCHSID to clipboard" ` +
    `title="Copy ${safe}">` +
    `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
    `<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>` +
    `<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>` +
    `</svg>` +
    `</button>` +
    `</span>`;
}

@Component({
  selector: 'cmt-calls',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AgGridAngular,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmCheckboxComponent,
    HlmAlertComponent,
    HlmTooltipDirective,
    IdAutocompleteComponent,
    ...HLM_MENU,
  ],
  templateUrl: './calls.component.html',
  styleUrl: './calls.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class CallsComponent {
  readonly callsService = inject(CallsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly employeeApi = inject(EmployeeApiService);
  private readonly searchService = inject(SearchService);
  private readonly loggingService = inject(LoggingService);
  private readonly callLookupService = inject(CallLookupService);

  /**
   * Search adapter for the Employee/Agent autocomplete — bound as the customSearchFn input
   * so the same IdAutocompleteComponent can drive Customer / UA (ES) and Employee (REST).
   * The arrow form preserves `this` when passed as a property reference.
   */
  readonly employeeSearchFn = (q: string): Observable<SearchResult[]> =>
    this.employeeApi.searchEmployees(q).pipe(map((list) => list.map(employeeToHit)));

  /** Reactive form — controls start mostly empty; the time block defaults to "Today" via constructor. */
  readonly filterForm = new FormGroup<FilterFormShape>(
    {
      customerId: new FormControl<string>('', { nonNullable: true }),
      userAccountId: new FormControl<string>('', { nonNullable: true }),
      agentId: new FormControl<string>('', { nonNullable: true }),
      singleDate: new FormControl<string>('', { nonNullable: true }),
      start: new FormControl<string>('', { nonNullable: true }),
      end: new FormControl<string>('', { nonNullable: true }),
    },
    { validators: [endAfterStart] },
  );

  /**
   * Time preset chips — `today` and `custom` are single-day; `1h`/`7d`/`30d` are ranges.
   * `today` is preselected on entry.
   */
  readonly timePresets: readonly TimePreset[] = [
    { key: 'today', label: 'Today', mode: 'single' },
    { key: '1h', label: 'Last hour', mode: 'range' },
    { key: '7d', label: 'Last 7 days', mode: 'range' },
    { key: '30d', label: 'Last 30 days', mode: 'range' },
    { key: 'custom', label: 'Custom', mode: 'range' },
  ];

  /** Currently-active preset key — null only after Reset. */
  readonly activePreset = signal<TimePresetKey | null>(null);

  /** Last selected Customer hit — used for cross-checking against UA's customerId + chip labels. */
  readonly selectedCustomerHit = signal<{ entityId: string; label: string } | null>(null);
  /**
   * Last selected hit from the UA/PS combined autocomplete. `kind` tells onSearch whether
   * to send the value as `userAccountId` or `phoneServiceId` to the calls endpoint, and
   * drives the active-filter chip label.
   */
  readonly selectedUserAccountHit = signal<{
    entityId: string;
    customerId: string;
    label: string;
    kind: 'ua' | 'ps';
  } | null>(null);
  /** Last selected Employee/Agent hit — used for chip-label display. */
  readonly selectedAgentHit = signal<{ entityId: string; label: string } | null>(null);

  /**
   * True while the Customer field holds a value that was auto-filled by a UA selection
   * (not entered by the user). Used to silently replace the value when the user picks a
   * different UA, instead of forcing them through the mismatch error path.
   */
  private readonly customerAutoFilled = signal<boolean>(false);
  /** Suppress the customerAutoFilled-reset side effect during programmatic setValue calls. */
  private suppressCustomerFlagReset = false;

  /**
   * Resolved-label computeds — feed each autocomplete the cached hit's name when its
   * form value matches, so re-expanding the filter card after a search shows
   * "<id> · <name>" instead of the bare ID.
   */
  readonly customerResolvedLabel = computed<string | null>(() => {
    const v = this.customerIdValue().trim();
    const h = this.selectedCustomerHit();
    return h && h.entityId === v && h.label ? h.label : null;
  });
  readonly userAccountResolvedLabel = computed<string | null>(() => {
    const v = this.userAccountIdValue().trim();
    const h = this.selectedUserAccountHit();
    return h && h.entityId === v && h.label ? h.label : null;
  });
  readonly agentResolvedLabel = computed<string | null>(() => {
    const v = this.agentIdValue().trim();
    const h = this.selectedAgentHit();
    return h && h.entityId === v && h.label ? h.label : null;
  });

  /** Whether the time block is in single-day mode (one date input) or range mode (from + to). */
  readonly dateMode = signal<DateMode>('single');

  /** Snapshot of filters that produced the loaded rows — drives the active-filter chip row. */
  readonly activeFilters = signal<ActiveFilters | null>(null);

  /**
   * Whether the filter card body is collapsed. We auto-collapse it after a successful search
   * that returned rows (there's no point reading filters again — chips above the grid recap them).
   * The user can still toggle it back open via the card header.
   */
  readonly filterCardCollapsed = signal(false);

  /** Toggle the filter card collapsed state. */
  toggleFilterCard(): void {
    this.filterCardCollapsed.update((v) => !v);
  }

  readonly quickFilter = signal('');

  /** Count of rows currently visible after the AG-Grid quick filter. Synced via gridApi events. */
  readonly visibleRowCount = signal(0);

  /**
   * True when the AG-Grid horizontal viewport scrolls — drives the right-edge fade
   * indicator on .cmt-grid-wrap so users know there's more data off-screen.
   * Synced from gridReady / gridSizeChanged / displayedColumnsChanged.
   */
  readonly hasHorizontalOverflow = signal(false);

  /** Free-text search input handler — bound to the hero-card search box. */
  onQuickFilterInput(event: Event): void {
    this.quickFilter.set((event.target as HTMLInputElement).value);
  }

  /** Mirror of the form as signals so the template reacts immediately without form-event wiring. */
  private readonly startValue = signal<string>('');
  private readonly endValue = signal<string>('');

  /** True if the selected range exceeds WARN_RANGE_DAYS — non-blocking. */
  readonly rangeWarn = computed<string | null>(() => {
    const s = this.startValue();
    const e = this.endValue();
    if (!s || !e) return null;
    const sd = new Date(s);
    const ed = new Date(e);
    if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime())) return null;
    const diffDays = (ed.getTime() - sd.getTime()) / MS_PER_DAY;
    return diffDays > WARN_RANGE_DAYS
      ? `Selected range exceeds ${WARN_RANGE_DAYS} days — the server response may be slow or large.`
      : null;
  });

  /** Form-level end-before-start error flag. */
  readonly hasEndBeforeStartError = computed<boolean>(() => {
    const s = this.startValue();
    const e = this.endValue();
    if (!s || !e) return false;
    return new Date(e) <= new Date(s);
  });

  /** Form fields, mirrored as signals so `hasAnyFilter` can react to all of them. */
  readonly customerIdValue = signal<string>('');
  readonly userAccountIdValue = signal<string>('');
  readonly agentIdValue = signal<string>('');
  readonly singleDateValue = signal<string>('');

  /** True iff at least one of {Customer, UA, Agent} is set — the identity-scope requirement. */
  readonly hasIdentityScope = computed<boolean>(() => {
    return (
      this.customerIdValue().trim() !== '' ||
      this.userAccountIdValue().trim() !== '' ||
      this.agentIdValue().trim() !== ''
    );
  });

  /** True iff a date is set (single-day or range). The other half of the minimum-search contract. */
  readonly hasDateScope = computed<boolean>(() => {
    return this.singleDateValue() !== '' || this.startValue() !== '' || this.endValue() !== '';
  });

  /** Backwards-compat alias used by chip rendering — kept since both scopes are covered above. */
  readonly hasAnyFilter = computed<boolean>(() => this.hasIdentityScope() || this.hasDateScope());

  /**
   * True when an autocomplete-backed field has free-text the user typed but never resolved
   * to a real entity (e.g. "peter frede" instead of selecting "192845 · Peter Frede" from the
   * dropdown). Blocks Search to prevent silently dropping the filter.
   */
  readonly hasUnresolvedAutocomplete = computed<boolean>(() => {
    const cust = this.customerIdValue().trim();
    const ua = this.userAccountIdValue().trim();
    const agent = this.agentIdValue().trim();
    const isNumericId = (s: string): boolean => s === '' || /^\d+$/.test(s);
    return !isNumericId(cust) || !isNumericId(ua) || !isNumericId(agent);
  });

  /**
   * True when both a Customer AND a User Account are selected, but the UA's `customerId`
   * does not belong to that Customer — the search would return zero rows by definition.
   */
  readonly hasCustomerUaMismatch = computed<boolean>(() => {
    const cust = this.customerIdValue().trim();
    const uaHit = this.selectedUserAccountHit();
    if (!cust || !uaHit) return false;
    return uaHit.customerId !== cust;
  });

  /**
   * Whether the Search button is enabled. We block ONLY on hard data errors so the user
   * always has a clickable Search affordance — soft "missing identity / missing date"
   * conditions surface as inline alerts and onSearch() early-returns instead of disabling
   * the button. Less noisy UX than a perpetually-greyed Search.
   */
  readonly canSearch = computed<boolean>(() => {
    if (this.hasEndBeforeStartError()) return false;
    if (this.hasUnresolvedAutocomplete()) return false;
    if (this.hasCustomerUaMismatch()) return false;
    return true;
  });

  /**
   * Pick the most descriptive name available — entity-aware so UAs surface the person's name
   * first ("Denise Schinagl") and customers surface the company. Falls back across fields so
   * we never end up with an empty chip.
   */
  private hitLabel(hit: SearchResult): string {
    const name = `${hit.firstName} ${hit.lastName}`.trim();
    if (hit.entityType === 'useraccount') {
      return name || hit.detail || hit.companyName || hit.entityId;
    }
    if (hit.entityType === 'phoneservice') {
      return hit.detail || hit.companyName || name || hit.entityId;
    }
    if (hit.entityType === 'employee') {
      return name || hit.detail || hit.entityId;
    }
    // Customer / default: company first, then person, then anything we have.
    return hit.companyName || name || hit.detail || hit.entityId;
  }

  /** Handler bound to the Customer autocomplete's hitSelected output — caches the hit metadata. */
  onCustomerHit(hit: SearchResult | null): void {
    this.selectedCustomerHit.set(hit ? { entityId: hit.entityId, label: this.hitLabel(hit) } : null);
  }

  /** Handler for the Agent/Employee autocomplete — caches name for chip rendering. */
  onAgentHit(hit: SearchResult | null): void {
    this.selectedAgentHit.set(hit ? { entityId: hit.entityId, label: this.hitLabel(hit) } : null);
  }

  /**
   * Handler for the UA autocomplete — caches the UA's parent customerId for the
   * Customer↔UA cross-check, and auto-fills (or replaces) the Customer field so the user
   * doesn't run into the mismatch alert on every UA change.
   *
   * Replace policy: we only overwrite the Customer field if it's empty OR was itself
   * auto-filled by a previous UA selection. A manually-entered Customer is sacred —
   * if it conflicts with the new UA the mismatch alert remains the right feedback.
   */
  onUserAccountHit(hit: SearchResult | null): void {
    if (!hit) {
      this.selectedUserAccountHit.set(null);
      // If the Customer field was auto-filled by the previous UA selection, clear it too —
      // keeps state consistent with the user's intent ("I removed the UA scope").
      if (this.customerAutoFilled()) {
        this.suppressCustomerFlagReset = true;
        this.filterForm.controls.customerId.setValue('');
        this.suppressCustomerFlagReset = false;
        this.selectedCustomerHit.set(null);
        this.customerAutoFilled.set(false);
      }
      return;
    }
    const kind: 'ua' | 'ps' = hit.entityType === 'phoneservice' ? 'ps' : 'ua';
    this.selectedUserAccountHit.set({
      entityId: hit.entityId,
      customerId: hit.customerId,
      label: this.hitLabel(hit),
      kind,
    });

    const currentCustomer = this.customerIdValue().trim();
    const canReplace = !currentCustomer || this.customerAutoFilled();
    if (canReplace) {
      this.suppressCustomerFlagReset = true;
      this.filterForm.controls.customerId.setValue(hit.customerId);
      this.suppressCustomerFlagReset = false;
      // companyName isn't always present on UA hits — fall back to empty label, the chip
      // will then just show the ID which is still informative.
      this.selectedCustomerHit.set({ entityId: hit.customerId, label: hit.companyName || '' });
      this.customerAutoFilled.set(true);
    }
    // else: user manually entered a different Customer — let hasCustomerUaMismatch surface it.
  }

  /** Warn when an Agent ID is given without any other identity scope — agent search alone is rarely useful. */
  readonly hasUnscopedAgentWarn = computed<boolean>(() => {
    const agent = this.filterForm.controls.agentId.value.trim();
    if (!agent) return false;
    const customer = this.filterForm.controls.customerId.value.trim();
    const ua = this.filterForm.controls.userAccountId.value.trim();
    const start = this.startValue();
    return !customer && !ua && !start;
  });

  /** Column chooser — visibleFields is the set of toggleable fields currently shown. */
  readonly visibleFields = signal<Set<string>>(this.loadInitialVisibleFields());

  /**
   * Column defs are built ONCE from the initial visibleFields — we do not re-render via
   * `[columnDefs]="columnDefs()"` on every toggle, because AG-Grid preserves its internal
   * column state (visibility, order, width) across columnDefs input changes and silently
   * ignores the new `hide` values. Instead, all subsequent visibility changes go through
   * `gridApi.setColumnsVisible()` in `toggleColumn`.
   */
  readonly columnDefs: ColDef<CallInformation>[] = this.buildColumnDefs(this.visibleFields());

  readonly defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: false,
    valueFormatter: nullAsDashFormatter,
    // Whole row is clickable (opens the Call Details dialog) — signal it on every cell.
    cellStyle: { cursor: 'pointer' },
    // Tooltip shows the formatted (or raw) value of the cell — surfaces full text whenever
    // a column is too narrow to display it. AG-Grid's tooltipShowDelay is set on the grid.
    tooltipValueGetter: (p) => {
      const v = p.valueFormatted ?? p.value;
      if (v == null || v === '' || v === '—') return null;
      return String(v);
    },
  };

  /**
   * Row class by status — gives BUSY / RING / Notiz a subtle row tint so they're
   * spottable at a glance, mirroring the old CMT call list. Other statuses stay
   * neutral so the tinted rows are the ones that visually pop.
   */
  readonly getRowClass = (params: { data?: CallInformation }): string => {
    const status = (params.data?.status ?? '').toUpperCase();
    if (status === 'BUSY') return 'cmt-row-busy';
    if (status === 'RING') return 'cmt-row-ring';
    if (status === 'NOTIZ') return 'cmt-row-notiz';
    return '';
  };

  /**
   * Quick tint filter — set of statuses the user has toggled on via the legend chips.
   * Empty set means no filter (show everything). Special value 'OTHER' matches anything
   * that isn't BUSY / RING / NOTIZ.
   */
  readonly tintFilter = signal<ReadonlySet<string>>(new Set<string>());

  /** Toggle a single tint chip on/off, then ask AG-Grid to re-evaluate the external filter. */
  toggleTintFilter(key: 'BUSY' | 'RING' | 'NOTIZ' | 'OTHER'): void {
    this.tintFilter.update((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    this.gridApi?.onFilterChanged();
  }

  /** Clear all tint chip selections — used by the 'X clear filter' action. */
  clearTintFilter(): void {
    if (this.tintFilter().size === 0) return;
    this.tintFilter.set(new Set());
    this.gridApi?.onFilterChanged();
  }

  /** AG-Grid hook: present an external filter whenever at least one tint chip is active. */
  readonly isExternalFilterPresent = (): boolean => this.tintFilter().size > 0;

  /**
   * AG-Grid hook: decide whether a row passes the active tint filter. A row matches if
   * its status maps to any of the selected chips. 'OTHER' covers everything that isn't
   * BUSY / RING / NOTIZ (e.g. "keiner", "Testanruf", "vbOrphan", null).
   */
  readonly doesExternalFilterPass = (node: { data?: CallInformation }): boolean => {
    const filters = this.tintFilter();
    if (filters.size === 0) return true;
    const status = (node.data?.status ?? '').toUpperCase();
    if (filters.has('BUSY') && status === 'BUSY') return true;
    if (filters.has('RING') && status === 'RING') return true;
    if (filters.has('NOTIZ') && status === 'NOTIZ') return true;
    if (filters.has('OTHER') && status !== 'BUSY' && status !== 'RING' && status !== 'NOTIZ') {
      return true;
    }
    return false;
  };

  /** Overlay template while loading. */
  /**
   * Loading overlay — animated indeterminate progress bar + hint.
   * Uses the global `animate-indeterminate` keyframe (declared in styles.tailwind.css)
   * so the visual matches `<hlm-progress />`. AG-Grid wants a plain HTML string here, so
   * we can't embed the Spartan component directly — same primitives, raw markup.
   */
  readonly loadingOverlayTemplate =
    '<div class="cmt-grid-loading flex w-full max-w-xl flex-col items-center gap-4 px-8 text-muted-foreground">' +
      '<div class="flex w-full flex-col items-center gap-2">' +
        '<div class="text-lg font-semibold text-foreground">Loading calls…</div>' +
        '<div class="text-sm">Large date ranges may take a few seconds.</div>' +
      '</div>' +
      '<div class="h-2 w-full overflow-hidden rounded-full bg-muted">' +
        '<div class="h-full w-1/3 animate-indeterminate rounded-full bg-primary"></div>' +
      '</div>' +
    '</div>';

  /** Overlay template shown when rows are empty. */
  readonly noRowsOverlayTemplate =
    '<div class="rounded-xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">No calls found — adjust the filters and press Search</div>';

  /** Metadata for the column chooser (label + field — used by the template). */
  readonly toggleableColumns = TOGGLEABLE_FIELDS.map((field) => ({
    field: field as string,
    label: TOGGLEABLE_FIELD_LABELS[field] ?? field,
  }));

  private gridApi?: GridApi<CallInformation>;

  constructor() {
    // EB-4038: when returning to the Call Viewer from another area via the rail, the URL
    // carries the same filters that produced the rows still held in the (root) CallsService.
    // In that case reuse them — no clear(), no refetch. Only a fresh deep-link, a hard
    // refresh, or different filters take the fetch path below.
    const urlQueryKey = callsQueryKey((field) => this.route.snapshot.queryParamMap.get(field));
    const canReuseLoadedRows = this.callsService.matchesLoaded(urlQueryKey);

    // No auto-search on load. Clear any previously-loaded rows from a prior session,
    // unless we are reusing rows that already match the URL (area-switch return).
    if (!canReuseLoadedRows) {
      this.callsService.clear();
    }

    // Sync signal mirrors from the form so computed signals (hasAnyFilter, etc.) react.
    this.filterForm.controls.start.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.startValue.set(v));
    this.filterForm.controls.end.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.endValue.set(v));
    this.filterForm.controls.customerId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => {
        this.customerIdValue.set(v);
        // User-driven changes (typing, clearing, manual selection) drop the auto-fill marker.
        // Suppressed only while we're programmatically replacing the auto-filled value.
        if (!this.suppressCustomerFlagReset) this.customerAutoFilled.set(false);
      });
    this.filterForm.controls.userAccountId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.userAccountIdValue.set(v));
    this.filterForm.controls.agentId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.agentIdValue.set(v));

    // Manual edits to start/end imply range mode and clear any active preset (except `custom`).
    this.filterForm.controls.start.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.suppressPresetReset) return;
        this.dateMode.set('range');
        // If singleDate was set by the previous mode, clear it so onSearch doesn't pick it up.
        if (this.filterForm.controls.singleDate.value) {
          this.suppressPresetReset = true;
          this.filterForm.controls.singleDate.setValue('');
          this.suppressPresetReset = false;
        }
        if (this.activePreset() !== 'custom') this.activePreset.set('custom');
      });
    this.filterForm.controls.end.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.suppressPresetReset) return;
        this.dateMode.set('range');
        if (this.filterForm.controls.singleDate.value) {
          this.suppressPresetReset = true;
          this.filterForm.controls.singleDate.setValue('');
          this.suppressPresetReset = false;
        }
        if (this.activePreset() !== 'custom') this.activePreset.set('custom');
      });
    this.filterForm.controls.singleDate.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => {
        this.singleDateValue.set(v);
        if (this.suppressPresetReset) return;
        // Manually editing the date switches to single mode, clears any range, highlights only when it's today.
        this.dateMode.set('single');
        if (this.filterForm.controls.start.value || this.filterForm.controls.end.value) {
          this.suppressPresetReset = true;
          this.filterForm.controls.start.setValue('');
          this.filterForm.controls.end.setValue('');
          this.suppressPresetReset = false;
        }
        this.activePreset.set(v === toLocalIsoDay(new Date()) ? 'today' : null);
      });

    // Hydrate form from URL queryParams on entry, OR fall back to "Today" preset as default.
    // Reuse path (area-switch return): rebuild filters WITHOUT re-running the search.
    if (canReuseLoadedRows) {
      this.hydrateFromQueryParams(false);
    } else {
      const hydrated = this.hydrateFromQueryParams();
      if (!hydrated) {
        this.applyTimePreset('today');
      }
    }

    /**
     * F2/F9: a `cchsid` in the URL opens its call — on page load AND on later changes while
     * this instance is reused. Subscribed after the hydrate above so the synchronous first
     * emission sees the hydrated filters; distinctUntilChanged keeps it to one dialog per
     * cchsid change; removing the consumed `cchsid` (removeCchsidFromUrl, or the list search)
     * resets it to '' so a later jump to the same cchsid opens it again.
     * @ticket EB-4153
     */
    this.route.queryParamMap
      .pipe(
        map((params, index) => ({ params, cchsid: params.get('cchsid')?.trim() ?? '', fromPageLoad: index === 0 })),
        distinctUntilChanged((prev, next) => prev.cchsid === next.cchsid),
        filter(({ cchsid }) => cchsid !== ''),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ params, fromPageLoad }) => this.openCallFromUrl(params, fromPageLoad));

    // Delegated click handler for the CCHSID cell copy-to-clipboard icon.
    // Cell renderer returns static HTML, so we bind once at the document level.
    const onCopyClick = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const btn = target.closest('.cmt-copy-cchsid') as HTMLButtonElement | null;
      if (!btn) return;
      event.stopPropagation();
      event.preventDefault();
      const value = btn.getAttribute('data-cchsid');
      if (!value) return;
      // Shows a "Copied to clipboard" toast (1.5s) via the shared helper.
      copyToClipboard(this.toastService, value, event);
      // Secondary in-cell visual feedback — flash primary color on the icon briefly.
      btn.classList.add('text-primary');
      setTimeout(() => btn.classList.remove('text-primary'), 1200);
    };
    document.addEventListener('click', onCopyClick, true);
    this.destroyRef.onDestroy(() => document.removeEventListener('click', onCopyClick, true));
  }

  /** When true, valueChanges from start/end/singleDate won't reset `activePreset`. */
  private suppressPresetReset = false;

  /** Build a relative range ending now — `[now - durationMs, now]`. */
  private relativeRange(durationMs: number): { start: string; end: string } {
    const now = new Date();
    const start = new Date(now.getTime() - durationMs);
    return { start: toLocalIsoMinute(start), end: toLocalIsoMinute(now) };
  }

  /** Switch to a preset chip — drives mode + form values together. */
  applyTimePreset(key: TimePresetKey): void {
    const preset = this.timePresets.find((p) => p.key === key);
    if (!preset) return;

    // Snapshot of the current time block — used by `custom` to keep an editable starting point
    // (instead of forcing the user to fill two empty datetime-local pickers from scratch).
    const prevStart = this.filterForm.controls.start.value;
    const prevEnd = this.filterForm.controls.end.value;
    const prevSingle = this.filterForm.controls.singleDate.value;

    this.suppressPresetReset = true;
    this.dateMode.set(preset.mode);

    if (preset.mode === 'single') {
      // Single-day mode — clear range fields, set singleDate.
      this.filterForm.controls.start.setValue('');
      this.filterForm.controls.end.setValue('');
      if (key === 'today') {
        this.filterForm.controls.singleDate.setValue(toLocalIsoDay(new Date()));
      }
    } else {
      // Range mode — clear singleDate first, then fill start/end based on the preset.
      this.filterForm.controls.singleDate.setValue('');
      if (key === '1h') {
        const r = this.relativeRange(MS_PER_HOUR);
        this.filterForm.controls.start.setValue(r.start);
        this.filterForm.controls.end.setValue(r.end);
      } else if (key === '7d') {
        const r = this.relativeRange(7 * MS_PER_DAY);
        this.filterForm.controls.start.setValue(r.start);
        this.filterForm.controls.end.setValue(r.end);
      } else if (key === '30d') {
        const r = this.relativeRange(30 * MS_PER_DAY);
        this.filterForm.controls.start.setValue(r.start);
        this.filterForm.controls.end.setValue(r.end);
      } else if (key === 'custom') {
        // Smart-default: prefer to keep what was there (range preset, single-day, or fully
        // empty) so the user only edits the boundary they actually want to change.
        if (prevStart && prevEnd) {
          // Coming from a range preset (1h / 7d / 30d) — keep its values as edit base.
          this.filterForm.controls.start.setValue(prevStart);
          this.filterForm.controls.end.setValue(prevEnd);
        } else if (prevSingle) {
          // Coming from single-day — expand the day to [singleDate 00:00, now].
          this.filterForm.controls.start.setValue(`${prevSingle}T00:00`);
          this.filterForm.controls.end.setValue(toLocalIsoMinute(new Date()));
        } else {
          // Truly empty — default to [today 00:00, now].
          const startOfDay = new Date();
          startOfDay.setHours(0, 0, 0, 0);
          this.filterForm.controls.start.setValue(toLocalIsoMinute(startOfDay));
          this.filterForm.controls.end.setValue(toLocalIsoMinute(new Date()));
        }
      }
    }

    this.suppressPresetReset = false;
    this.activePreset.set(key);
  }

  onGridReady(event: GridReadyEvent<CallInformation>): void {
    this.gridApi = event.api;
    // Re-assert persisted visibility — AG-Grid may not honor initial `hide` values
    // when the grid remounts after HMR or when localStorage loaded after first render.
    this.syncGridColumnVisibility();
    // Force cell refresh whenever new rowData is applied — AG-Grid v32+ sometimes
    // skips function-based string cellRenderers on the initial pass, leaving the
    // custom badge / link cells blank.
    event.api.addEventListener('rowDataUpdated', () => {
      event.api.refreshCells({ force: true });
    });
    // Track visible (post-filter) row count so the result-counter can show "X of Y visible".
    event.api.addEventListener('modelUpdated', () => {
      this.visibleRowCount.set(event.api.getDisplayedRowCount());
    });
    // Recompute horizontal overflow whenever the column set or sizes change.
    event.api.addEventListener('displayedColumnsChanged', () => this.recomputeOverflow());
    event.api.addEventListener('virtualColumnsChanged', () => this.recomputeOverflow());
  }

  /** Read the AG-Grid center-cols viewport and toggle the overflow flag. Called from grid events. */
  private recomputeOverflow(): void {
    const v = document.querySelector('.cmt-grid .ag-center-cols-viewport') as HTMLElement | null;
    if (!v) return;
    this.hasHorizontalOverflow.set(v.scrollWidth > v.clientWidth + 1);
  }

  /** Push the current visibleFields set into AG-Grid via the imperative API. */
  private syncGridColumnVisibility(): void {
    const api = this.gridApi;
    if (!api) return;
    const visible = this.visibleFields();
    const toShow: string[] = [];
    const toHide: string[] = [];
    for (const field of TOGGLEABLE_FIELDS) {
      if (visible.has(field as string)) toShow.push(field as string);
      else toHide.push(field as string);
    }
    if (toShow.length) api.setColumnsVisible(toShow, true);
    if (toHide.length) api.setColumnsVisible(toHide, false);
  }

  onFirstDataRendered(event: FirstDataRenderedEvent<CallInformation>): void {
    event.api.sizeColumnsToFit();
    // AG-Grid v32+ sometimes skips function-based string cellRenderers on the initial
    // render pass; a forced refresh guarantees all badges/links show up consistently.
    event.api.refreshCells({ force: true });
  }

  onGridSizeChanged(event: GridSizeChangedEvent): void {
    event.api.sizeColumnsToFit();
    this.recomputeOverflow();
  }

  onSearch(): void {
    // Hard errors block — Button is also visually disabled in these cases.
    if (!this.canSearch()) return;
    // Soft requirements — Button stays enabled, but click is a no-op until met.
    // The corresponding inline alerts in the template surface the missing pieces.
    if (!this.hasIdentityScope() || !this.hasDateScope()) return;

    const customerIdRaw = this.filterForm.controls.customerId.value.trim();
    const uaRaw = this.filterForm.controls.userAccountId.value.trim();
    const agentRaw = this.filterForm.controls.agentId.value.trim();
    const singleDateRaw = this.filterForm.controls.singleDate.value;
    const startRaw = this.filterForm.controls.start.value;
    const endRaw = this.filterForm.controls.end.value;

    const customerId = customerIdRaw !== '' ? customerIdRaw : undefined;
    // The UA field can hold either a UA-ID or a PS-ID — kind comes from the last picked hit.
    const uaValueNum =
      uaRaw !== '' && !Number.isNaN(Number(uaRaw)) ? Number(uaRaw) : undefined;
    const uaPsKind = this.selectedUserAccountHit()?.kind ?? 'ua';
    const userAccountId = uaPsKind === 'ua' ? uaValueNum : undefined;
    const phoneServiceId = uaPsKind === 'ps' ? uaValueNum : undefined;
    const agentId =
      agentRaw !== '' && !Number.isNaN(Number(agentRaw)) ? Number(agentRaw) : undefined;

    // Derive start/end Dates depending on mode.
    let start: Date | undefined;
    let end: Date | undefined;
    if (this.dateMode() === 'single' && singleDateRaw !== '') {
      // Single-day mode: full local day [00:00, 23:59:59].
      start = new Date(`${singleDateRaw}T00:00:00`);
      end = new Date(`${singleDateRaw}T23:59:59`);
    } else {
      start = startRaw !== '' ? new Date(startRaw) : undefined;
      end = endRaw !== '' ? new Date(endRaw) : undefined;
    }

    // Pre-flight large-range guard — confirm if range > WARN_RANGE_DAYS and not opted out this session.
    const rangeDays = start && end ? (end.getTime() - start.getTime()) / MS_PER_DAY : 0;
    const optedOut = sessionStorage.getItem(LARGE_RANGE_OPT_OUT_KEY) === 'true';
    if (rangeDays > WARN_RANGE_DAYS && !optedOut) {
      const ref = this.dialog.open<
        LargeRangeConfirmDialogComponent,
        LargeRangeConfirmData,
        LargeRangeConfirmResult
      >(LargeRangeConfirmDialogComponent, {
        data: { rangeText: `${Math.round(rangeDays)} days` },
        panelClass: 'cmt-dialog-panel',
        autoFocus: 'first-tabbable',
      });
      ref.afterClosed().subscribe((result) => {
        if (!result?.proceed) return;
        if (result.remember) sessionStorage.setItem(LARGE_RANGE_OPT_OUT_KEY, 'true');
        this.executeSearch({
          start, end, customerId, userAccountId, phoneServiceId, agentId,
          uaRaw, agentRaw, singleDateRaw, uaPsKind,
        });
      });
      return;
    }

    this.executeSearch({
      start, end, customerId, userAccountId, phoneServiceId, agentId,
      uaRaw, agentRaw, singleDateRaw, uaPsKind,
    });
  }

  /** Actually run the search — separated so the large-range confirm modal can gate it. */
  private executeSearch(p: {
    start?: Date; end?: Date;
    customerId?: string;
    userAccountId?: number; phoneServiceId?: number; agentId?: number;
    uaRaw: string; agentRaw: string; singleDateRaw: string;
    uaPsKind: 'ua' | 'ps';
  }): void {
    // Capture snapshot for active-filter chips + URL persistence.
    const presetKey = this.activePreset();
    let presetLabel: string | undefined;
    if (this.dateMode() === 'single' && p.singleDateRaw !== '') {
      presetLabel = presetKey === 'today' ? 'Today' : `On ${p.singleDateRaw}`;
    } else if (presetKey === 'custom' && p.start && p.end) {
      // Custom range: show the actual From/To so the chip is informative on its own.
      // Format "YYYY-MM-DD HH:mm → YYYY-MM-DD HH:mm" — locale-agnostic, sortable, compact.
      const fmt = (d: Date): string => `${toLocalIsoDay(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      presetLabel = `${fmt(p.start)} → ${fmt(p.end)}`;
    } else if (presetKey) {
      presetLabel = this.timePresets.find((x) => x.key === presetKey)?.label;
    }
    // Resolve labels from the cached selected hits — only if the ID still matches the typed
    // value (user might have typed an ID directly without picking from the dropdown).
    const custHit = this.selectedCustomerHit();
    const uaHit = this.selectedUserAccountHit();
    const agentHit = this.selectedAgentHit();
    const customerLabel =
      custHit && custHit.entityId === p.customerId ? custHit.label || undefined : undefined;
    const userAccountLabel =
      uaHit && uaHit.entityId === p.uaRaw ? uaHit.label || undefined : undefined;
    const agentLabel =
      agentHit && agentHit.entityId === p.agentRaw ? agentHit.label || undefined : undefined;

    this.activeFilters.set({
      customerId: p.customerId,
      customerLabel,
      userAccountId: p.uaRaw !== '' && p.uaPsKind === 'ua' ? p.uaRaw : undefined,
      userAccountLabel: p.uaPsKind === 'ua' ? userAccountLabel : undefined,
      phoneServiceId: p.uaRaw !== '' && p.uaPsKind === 'ps' ? p.uaRaw : undefined,
      phoneServiceLabel: p.uaPsKind === 'ps' ? userAccountLabel : undefined,
      agentId: p.agentRaw !== '' ? p.agentRaw : undefined,
      agentLabel,
      start: p.start ? toLocalIsoMinute(p.start) : undefined,
      end: p.end ? toLocalIsoMinute(p.end) : undefined,
      rangePresetLabel: presetLabel,
    });
    const queryRecord = this.syncQueryParams();
    // EB-4038: signature of this search — stored on success so a later area-switch return
    // with the same URL can reuse the rows instead of refetching.
    const loadedKey = callsQueryKey((field) => queryRecord[field]);

    // Clear previous rows BEFORE the next request — old data sitting behind the
    // signal-driven loading overlay would peek through during the wait.
    // The overlay itself (.cmt-grid-wrap > absolute z-10 div, gated by
    // callsService.loading()) renders synchronously and doesn't depend on
    // gridApi being ready, so it works on the very first search too.
    this.callsService.clear();
    this.callsService
      .findCallInformation({
        start: p.start,
        end: p.end,
        customerId: p.customerId,
        agentId: p.agentId,
        userAccountId: p.userAccountId,
        phoneServiceId: p.phoneServiceId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          // Record the signature of the rows now loaded so returning to the Call Viewer
          // from another area with the same URL reuses them (EB-4038). Note: the mount-time
          // reuse guard also requires rows>0, so a 0-row result still refetches on return
          // (rare, and avoids reasoning about the empty-grid overlay on the reuse path).
          this.callsService.markLoaded(loadedKey);
          if (rows.length === 0) {
            this.gridApi?.showNoRowsOverlay();
          } else {
            this.gridApi?.hideOverlay();
            // Auto-collapse the filter card only when there's actually a result to look at —
            // empty searches leave it open so the user can refine without an extra click.
            this.filterCardCollapsed.set(true);
          }
        },
        // No `error` handler needed: callsService.loading() flips to false in its
        // finalize(), which hides the signal-driven overlay. The error banner
        // above the grid covers the user-facing message.
      });
  }

  /**
   * Chip actions from the active-filter row:
   *  - identity chips (customer / UA-PS / agent) are REMOVED (field cleared);
   *  - the time chip is RESET to the default "Today" (a date is mandatory, so it can't
   *    be removed — the chip uses a distinct reset affordance, not an ✕).
   *
   * A search needs an identity AND a date. If both still hold after the change we re-run
   * the search in place (results just update — no filter-card flash). If the change left
   * the search invalid (no identity), we drop the stale results and open the card so the
   * user sees the "add identity" hint and can pick a new scope.
   */
  removeActiveFilter(field: keyof ActiveFilters): void {
    if (field === 'rangePresetLabel' || field === 'start' || field === 'end') {
      // Reset the time scope to the default "Today" (not a removal).
      this.applyTimePreset('today');
    } else if (field === 'customerId') {
      this.filterForm.controls.customerId.setValue('');
    } else if (field === 'userAccountId') {
      this.filterForm.controls.userAccountId.setValue('');
      this.selectedUserAccountHit.set(null);
      // Mirror onUserAccountHit(null): a Customer that was auto-filled by this UA/PS must
      // not linger as a surprise broader scope once the UA/PS chip is removed.
      if (this.customerAutoFilled()) {
        this.suppressCustomerFlagReset = true;
        this.filterForm.controls.customerId.setValue('');
        this.suppressCustomerFlagReset = false;
        this.selectedCustomerHit.set(null);
        this.customerAutoFilled.set(false);
      }
    } else if (field === 'agentId') {
      this.filterForm.controls.agentId.setValue('');
    }

    // setValue/applyTimePreset emit synchronously, so the scope signals are already current.
    if (this.canSearch() && this.hasIdentityScope() && this.hasDateScope()) {
      // Still a valid search — refresh in place, card stays as-is (no open/close flash).
      this.onSearch();
    } else {
      // Removal left no valid search: clear the stale results and reveal the form + hint.
      this.activeFilters.set(null);
      this.callsService.clear();
      this.syncQueryParams();
      if (this.filterCardCollapsed()) this.toggleFilterCard();
    }
  }

  onExportCsv(): void {
    const customerIdRaw = this.filterForm.controls.customerId.value.trim();
    const fileCustomer = customerIdRaw !== '' ? customerIdRaw : 'all';
    const stamp = new Date();
    const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
    const today = `${stamp.getFullYear()}${pad(stamp.getMonth() + 1)}${pad(stamp.getDate())}`;
    this.gridApi?.exportDataAsCsv({
      fileName: `calls-${fileCustomer}-${today}.csv`,
      columnKeys: [...ALL_24_FIELD_NAMES] as string[],
      processCellCallback: (p) => (p.value == null ? '' : String(p.value)),
    });
  }

  /** Build the canonical sharable URL — current location with all filter query params. */
  private currentShareUrl(): string {
    return typeof window !== 'undefined' ? window.location.href : '';
  }

  /** Subject / message body shared between e-mail + Teams variants. */
  private shareMessageText(): string {
    const af = this.activeFilters();
    const parts: string[] = [];
    if (af?.customerLabel || af?.customerId) parts.push(`Customer ${af.customerLabel ?? af.customerId}`);
    if (af?.userAccountLabel || af?.userAccountId) parts.push(`UA ${af.userAccountLabel ?? af.userAccountId}`);
    if (af?.phoneServiceLabel || af?.phoneServiceId) parts.push(`PS ${af.phoneServiceLabel ?? af.phoneServiceId}`);
    if (af?.agentLabel || af?.agentId) parts.push(`Agent ${af.agentLabel ?? af.agentId}`);
    if (af?.rangePresetLabel) parts.push(af.rangePresetLabel);
    return parts.length ? `Call viewer search: ${parts.join(', ')}` : 'Call viewer search';
  }

  /** Copy the URL to the clipboard. */
  onShareCopy(): void {
    const url = this.currentShareUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => this.toastService.open('Link copied to clipboard', '', { duration: 1800 }),
      () => this.toastService.open('Could not copy link', 'Check browser permissions', { duration: 2500, tone: 'error' }),
    );
  }

  /** Open the user's default mail client with a prefilled subject + body. */
  onShareEmail(): void {
    const url = this.currentShareUrl();
    if (!url) return;
    const subject = this.shareMessageText();
    const body = `${subject}\n\n${url}`;
    const href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  }

  /**
   * Open the Teams share dialog with the URL + a message attached. We use the HTTPS
   * variant (teams.microsoft.com/share) instead of the `msteams:` protocol because the
   * latter opens the app but drops `href` / `msgText` payloads in practice. The web
   * variant always carries the content; Teams itself offers an "Open in app" handoff
   * for users with the desktop app installed.
   */
  onShareTeams(): void {
    const url = this.currentShareUrl();
    if (!url) return;
    const msg = this.shareMessageText();
    const teamsUrl = `https://teams.microsoft.com/share?href=${encodeURIComponent(url)}&msgText=${encodeURIComponent(msg)}`;
    /** @ticket EB-3798 — usage: share the calls view to Microsoft Teams (side entrance). */
    this.loggingService.usage('side_entrance.click', { target: 'teams', section: 'calls' });
    window.open(teamsUrl, '_blank', 'noopener');
  }

  /** Reset the column-chooser preferences to the default visible set. Persists empty prefs. */
  resetColumns(): void {
    // DEFAULT_VISIBLE_FIELDS are the always-visible columns; toggleable columns start hidden.
    this.visibleFields.set(new Set<string>());
    this.callsService.saveColumnPrefs({ visibleFields: [] });
    // Push the new visibility into AG-Grid so the change is immediate.
    const api = this.gridApi;
    if (api) {
      const toShow: string[] = [];
      const toHide: string[] = [];
      for (const field of TOGGLEABLE_FIELDS) {
        toHide.push(field as string);
      }
      for (const field of DEFAULT_VISIBLE_FIELDS) {
        toShow.push(field as string);
      }
      api.setColumnsVisible(toShow, true);
      api.setColumnsVisible(toHide, false);
    }
  }

  toggleColumn(field: string, on: boolean): void {
    this.visibleFields.update((prev) => {
      const next = new Set(prev);
      if (on) next.add(field);
      else next.delete(field);
      return next;
    });
    this.callsService.saveColumnPrefs({ visibleFields: Array.from(this.visibleFields()) });
    // Imperative update — the `[columnDefs]` binding is one-shot, so we drive AG-Grid directly.
    this.gridApi?.setColumnsVisible([field], on);
  }

  isColumnVisible(field: string): boolean {
    return this.visibleFields().has(field);
  }

  /** Compact Berlin "HH:mm" of when the loaded rows were fetched — null until a load. */
  readonly loadedAtLabel = computed<string | null>(() => {
    const d = this.callsService.loadedAt();
    return d ? BERLIN_TIME_FMT.format(d) : null;
  });

  /** Full Berlin timestamp for the freshness-stamp tooltip. */
  readonly loadedAtFull = computed<string>(() => {
    const d = this.callsService.loadedAt();
    return d ? `Loaded ${formatBerlin(d.toISOString())} (Berlin)` : '';
  });

  onRetry(): void {
    this.onSearch();
  }

  /** Re-run the current search to refresh the loaded rows (freshness reload). */
  reloadCalls(): void {
    this.onSearch();
  }

  /** Clear all filter fields, the quick-filter, and any loaded rows. Confirms when results are loaded. */
  onReset(): void {
    if (this.callsService.rows().length > 0) {
      this.dialog
        .open<ConfirmDialogComponent, import('../../shared/confirm-dialog/confirm-dialog.component').ConfirmDialogData, boolean>(
          ConfirmDialogComponent,
          {
            data: {
              title: 'Reset filters',
              message: 'Discard the current results and clear all filters?',
              confirmLabel: 'Reset',
              cancelLabel: 'Keep results',
              confirmColor: 'warn',
              icon: 'restart_alt',
            },
            panelClass: 'cmt-dialog-panel',
            autoFocus: 'first-tabbable',
          },
        )
        .afterClosed()
        .subscribe((ok) => {
          if (ok) this.performReset();
        });
      return;
    }
    this.performReset();
  }

  /** Inner reset — extracted so the confirm dialog can call it once the user confirms. */
  private performReset(): void {
    this.clearFilterState();
    this.callsService.clear();
    this.gridApi?.hideOverlay();
    // Re-open the filter card so the user can edit filters again immediately.
    this.filterCardCollapsed.set(false);
    // Reset to "Today" default rather than empty time block.
    this.applyTimePreset('today');
    this.syncQueryParams();
  }

  /**
   * Empty the filter form, quick filter, active-filter snapshot and cached hits — shared by
   * {@link performReset} and the EB-4153 live URL path ({@link rehydrateFiltersFromUrl}).
   */
  private clearFilterState(): void {
    this.suppressPresetReset = true;
    this.filterForm.reset({
      customerId: '',
      userAccountId: '',
      agentId: '',
      singleDate: '',
      start: '',
      end: '',
    });
    this.suppressPresetReset = false;
    this.quickFilter.set('');
    this.activeFilters.set(null);
    this.selectedCustomerHit.set(null);
    this.selectedUserAccountHit.set(null);
    this.selectedAgentHit.set(null);
    this.customerAutoFilled.set(false);
  }

  /**
   * Read filter values from the URL queryParams (deep-link / refresh) on first load, or when a
   * new `cchsid` URL arrives on the live instance (EB-4153 F9, via rehydrateFiltersFromUrl).
   * Returns true when at least one param was applied (so the constructor knows whether
   * to fall back to the default "Today" preset).
   *
   * @param autoSearch when true (deep-link / refresh) the search runs automatically once
   *   the form is hydrated. When false (EB-4038 area-switch return) the already-loaded rows
   *   are reused: the filter chips are rebuilt from the URL and no fetch is triggered.
   */
  private hydrateFromQueryParams(autoSearch = true): boolean {
    const params = this.route.snapshot.queryParamMap;
    const patch: Partial<Record<keyof FilterFormShape, string>> = {};
    for (const key of [
      'customerId', 'userAccountId', 'agentId',
      'singleDate', 'start', 'end',
    ] as const) {
      const v = params.get(key);
      if (v) patch[key] = v;
    }
    // phoneServiceId arrives via its own URL key but maps onto the shared userAccountId form
    // control — kind is recovered through the seeded selectedUserAccountHit (ps).
    const psFromUrl = params.get('phoneServiceId');
    if (psFromUrl) patch.userAccountId = psFromUrl;

    if (Object.keys(patch).length === 0 && !psFromUrl) return false;

    this.suppressPresetReset = true;
    this.filterForm.patchValue(patch);
    this.suppressPresetReset = false;

    // Mode is inferred from which date fields are present.
    if (patch.singleDate) {
      this.dateMode.set('single');
      this.activePreset.set(patch.singleDate === toLocalIsoDay(new Date()) ? 'today' : null);
    } else if (patch.start || patch.end) {
      this.dateMode.set('range');
      this.activePreset.set('custom');
    }

    // Seed a minimal selectedUserAccountHit for PS so onSearch routes correctly. The label
    // arrives later via resolveAndApplyLabels (we don't have it from the URL alone).
    if (psFromUrl) {
      this.selectedUserAccountHit.set({
        entityId: psFromUrl,
        customerId: patch.customerId ?? '',
        label: '',
        kind: 'ps',
      });
    }

    if (!autoSearch) {
      // EB-4038 reuse path: rows are already loaded and match the URL. Rebuild the
      // active-filter chips from the hydrated form (so they show without a search),
      // collapse the filter card as after a search, and resolve labels async. No fetch.
      // A `cchsid` in the URL is handled independently by openCallFromUrl() (EB-4153).
      this.filterCardCollapsed.set(true);
      this.restoreActiveFiltersFromForm();
      this.resolveAndApplyLabels();
      return true;
    }

    // Resolve names asynchronously so the chips read "Name (ID)" instead of bare IDs after
    // refresh. Fires in parallel with the auto-search; activeFilters is enriched as labels
    // arrive.
    this.resolveAndApplyLabels();

    // Auto-run the search when arriving from a deep link.
    queueMicrotask(() => this.onSearch());
    return true;
  }

  /**
   * EB-4038 reuse path helper: rebuild the active-filter chip snapshot from the already-
   * hydrated form + date mode/preset, without running a search. Mirrors the snapshot
   * executeSearch() would produce; labels are upgraded async by resolveAndApplyLabels().
   */
  private restoreActiveFiltersFromForm(): void {
    const uaRaw = this.userAccountIdValue().trim();
    const uaKind = this.selectedUserAccountHit()?.kind ?? 'ua';
    const isSingle = this.dateMode() === 'single';
    const single = this.filterForm.controls.singleDate.value;
    const start = this.filterForm.controls.start.value;
    const end = this.filterForm.controls.end.value;
    const presetKey = this.activePreset();

    let presetLabel: string | undefined;
    if (isSingle && single) {
      presetLabel = presetKey === 'today' ? 'Today' : `On ${single}`;
    } else if (presetKey === 'custom' && start && end) {
      presetLabel = `${start.replace('T', ' ')} → ${end.replace('T', ' ')}`;
    } else if (presetKey) {
      presetLabel = this.timePresets.find((x) => x.key === presetKey)?.label;
    }

    // In single-day mode executeSearch stores the day boundaries in start/end (00:00–23:59);
    // mirror that here. The active-filter time chip is rendered on `af.start || af.end`
    // (calls.component.html), so leaving them empty in single mode would hide the "Today"
    // chip on the reuse path even though rangePresetLabel is set (EB-4038 bug fix).
    const singleStart = isSingle && single ? `${single}T00:00` : undefined;
    const singleEnd = isSingle && single ? `${single}T23:59` : undefined;

    this.activeFilters.set({
      customerId: this.customerIdValue().trim() || undefined,
      userAccountId: uaRaw !== '' && uaKind === 'ua' ? uaRaw : undefined,
      phoneServiceId: uaRaw !== '' && uaKind === 'ps' ? uaRaw : undefined,
      agentId: this.agentIdValue().trim() || undefined,
      start: isSingle ? singleStart : start || undefined,
      end: isSingle ? singleEnd : end || undefined,
      rangePresetLabel: presetLabel,
    });
  }


  /**
   * Mirror the active filters into URL queryParams so refresh / sharing preserve state.
   * Returns the query-param record so callers can derive the loaded-query key (EB-4038).
   */
  private syncQueryParams(): Record<CallFilterField, string | null> & { cchsid: null; tab: null } {
    const af = this.activeFilters();
    const isSingle = this.dateMode() === 'single';
    const singleDate = this.filterForm.controls.singleDate.value;
    // Typed against CALL_FILTER_FIELDS: omitting a filter field here is a compile error,
    // so the URL and the reuse key (callsQueryKey) can never silently diverge (EB-4038, review Med 3).
    const queryParams: Record<CallFilterField, string | null> & { cchsid: null; tab: null } = {
      customerId: af?.customerId ?? null,
      userAccountId: af?.userAccountId ?? null,
      phoneServiceId: af?.phoneServiceId ?? null,
      agentId: af?.agentId ?? null,
      singleDate: isSingle && singleDate ? singleDate : null,
      start: !isSingle ? af?.start ?? null : null,
      end: !isSingle ? af?.end ?? null : null,
      // Drop dialog-deep-link markers on every regular search so they don't keep re-opening
      // the dialog on subsequent searches. The dialog's own Share button re-injects them
      // when the user explicitly shares.
      cchsid: null,
      tab: null,
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    return queryParams;
  }

  /**
   * After hydrating from URL queryParams the IDs are present but the labels aren't
   * (the hits live in ES / the employee endpoint, not in the URL). Fire the lookups
   * now and patch the active-filter snapshot as each one resolves so the chips
   * upgrade from bare IDs to "Name (ID)" without a re-search.
   */
  private resolveAndApplyLabels(): void {
    const customerId = this.customerIdValue().trim();
    const uaOrPs = this.userAccountIdValue().trim();
    const agentId = this.agentIdValue().trim();
    const uaKind = this.selectedUserAccountHit()?.kind ?? 'ua';

    if (customerId) {
      this.searchService
        .quickSearch(customerId, { entityFilters: ['customer'], fieldFilters: ['customerId'] })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((hits) => {
          const h = hits.find((r) => r.entityId === customerId);
          if (!h) return;
          const label = this.hitLabel(h);
          this.selectedCustomerHit.set({ entityId: h.entityId, label });
          this.activeFilters.update((af) => (af ? { ...af, customerLabel: label } : af));
        });
    }

    if (uaOrPs) {
      const entityFilters: SearchEntityFilter[] = uaKind === 'ps' ? ['phoneservice'] : ['useraccount'];
      const fieldFilters = uaKind === 'ps' ? (['phoneServiceId'] as const) : (['userAccountId'] as const);
      this.searchService
        .quickSearch(uaOrPs, { entityFilters, fieldFilters: [...fieldFilters] })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((hits) => {
          const h = hits.find((r) => r.entityId === uaOrPs);
          if (!h) return;
          const label = this.hitLabel(h);
          this.selectedUserAccountHit.set({
            entityId: h.entityId,
            customerId: h.customerId,
            label,
            kind: uaKind,
          });
          this.activeFilters.update((af) => {
            if (!af) return af;
            return uaKind === 'ps'
              ? { ...af, phoneServiceLabel: label }
              : { ...af, userAccountLabel: label };
          });
        });
    }

    if (agentId) {
      this.employeeApi
        .searchEmployees(agentId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((employees) => {
          const e = employees.find((emp) => String(emp.id) === agentId);
          if (!e) return;
          const hit = employeeToHit(e);
          const label = this.hitLabel(hit);
          this.selectedAgentHit.set({ entityId: hit.entityId, label });
          this.activeFilters.update((af) => (af ? { ...af, agentLabel: label } : af));
        });
    }
  }

  /** Whole-row click → open Call Details dialog. Skips clicks that hit the copy-cchsid icon. */
  onRowClicked(event: { data?: CallInformation; event?: Event }): void {
    if (!event.data) return;
    const target = event.event?.target as HTMLElement | null;
    if (target?.closest('.cmt-copy-cchsid')) return;
    this.openCallDetails(event.data);
  }

  /** In-flight lookup started by {@link openCallFromUrl} — cancelled when a newer cchsid arrives. */
  private callFromUrlLookup?: Subscription;

  /**
   * Open the Call Details dialog for the call named by `cchsid` in the URL (Share link,
   * list deep link, unified-search jump) by loading it from the single-call endpoint.
   *
   * - F1: replaces the old one-shot match of the URL cchsid against the loaded list rows;
   *   a failed lookup shows a notice only, no row fallback.
   * - F2: runs independently of {@link hydrateFromQueryParams}, so the reuse path (same
   *   customer + day already loaded) and a cchsid-only URL (A6, no list search) open it too.
   * - F9: driven by the `queryParamMap` subscription, so a jump while already on `/calls`
   *   (component instance reused) opens the new call and re-hydrates the filters to its
   *   customer + day. F10: an open Call Details dialog is closed first (no stacking, E7).
   *
   * @param fromPageLoad true for the first emission — the constructor already hydrated the filters.
   * @ticket EB-4153
   */
  private openCallFromUrl(params: ParamMap, fromPageLoad: boolean): void {
    const cchsid = params.get('cchsid')?.trim();
    if (!cchsid) return;
    const tab = params.get('tab') as CallDetailsTab | null;

    if (!fromPageLoad) this.rehydrateFiltersFromUrl(params);

    this.dialog.openDialogs
      .filter((ref) => ref.componentInstance instanceof CallDetailsDialogComponent)
      .forEach((ref) => ref.close());

    this.callFromUrlLookup?.unsubscribe();
    this.callFromUrlLookup = this.callLookupService
      .findCallByCchsid(cchsid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        const scope = this.urlLookupScope(cchsid);
        if (!scope) return;
        if (scope.stripFromUrl) this.removeCchsidFromUrl();
        if (result.status === 'found') {
          // `?? null`: a call without customer (A6) must not borrow the list filter's customer.
          this.openCallDetails(result.call, tab ?? undefined, result.call.customerId ?? null);
        } else if (result.status === 'not-found') {
          this.toastService.open(CALL_NOT_AVAILABLE_TEXT, 'OK', { duration: 4000, tone: 'error' });
        } else {
          this.toastService.open(CALL_LOAD_ERROR_TEXT, 'OK', { duration: 4000, tone: 'error' });
        }
      });
  }

  /**
   * Decide, when the lookup for `cchsid` resolves, whether its outcome still applies. The
   * response is network-bound, so meanwhile the operator may have started another navigation
   * (away from the Call Viewer, or a jump to a newer cchsid whose `queryParamMap` emission
   * has not arrived yet). Both the committed URL (`router.url`) and the target of a pending
   * navigation are checked, so a late response does not act on a URL or a pending navigation that has moved to another cchsid or left the Call Viewer. It cannot tell the list search's own URL update apart from later operator actions on /calls.
   *
   * @returns `null` when the lookup is stale — a URL is off the Call Viewer or carries a
   *   different cchsid: no strip, no dialog, no toast. Otherwise `stripFromUrl` is true only
   *   while the consumed cchsid is still in the URL and no pending navigation removes it.
   * @ticket EB-4153
   */
  private urlLookupScope(cchsid: string): { stripFromUrl: boolean } | null {
    const committed = this.router.parseUrl(this.router.url);
    const pending = this.router.currentNavigation();
    const target = pending ? (pending.finalUrl ?? pending.extractedUrl) : committed;
    const urls = [committed, target];
    if (urls.some((url) => !isCallViewerUrl(url))) return null;
    const urlCchsids = urls.map((url) => url.queryParamMap.get('cchsid')?.trim() ?? '');
    if (urlCchsids.some((value) => value !== '' && value !== cchsid)) return null;
    return { stripFromUrl: urlCchsids.every((value) => value === cchsid) };
  }

  /**
   * Drop the consumed `cchsid` + `tab` from the URL (any lookup outcome, but only while
   * {@link urlLookupScope} says the consumed cchsid is still in the URL), consistent with F8 —
   * sharing rests on the dialog's own link. Without it, paths where no list search strips
   * them (cchsid-only URL, reuse path, failed search) keep `cchsid` in the URL, so a re-jump
   * to the same call is a same-URL navigation the router ignores and no dialog opens. The
   * resulting emission carries no cchsid, so it opens nothing and resets distinctUntilChanged.
   * @ticket EB-4153
   */
  private removeCchsidFromUrl(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { cchsid: null, tab: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * F9 live path: the URL switched to another call while this instance is alive. Replace the
   * filter form with the URL's filters (the jump link carries only customer + day, so stale
   * UA / agent / range values must not narrow the new search), then hydrate — reusing the
   * loaded rows when they already match. A cchsid-only URL (A6) leaves the list untouched.
   * @ticket EB-4153
   */
  private rehydrateFiltersFromUrl(params: ParamMap): void {
    const urlKey = callsQueryKey((field) => params.get(field));
    if (urlKey === '') return;
    const canReuseLoadedRows = this.callsService.matchesLoaded(urlKey);
    this.clearFilterState();
    if (!canReuseLoadedRows) {
      // The previous customer's rows must not stay on screen while (or if) the new search runs.
      this.callsService.clear();
      this.gridApi?.hideOverlay();
    }
    this.hydrateFromQueryParams(!canReuseLoadedRows);
  }

  /**
   * Open the Call Details dialog for a given row, optionally landing on a specific tab.
   * @param customerId override for the dialog's jump buttons (F3, URL path: the looked-up
   *   call's own customer, `null` = none). Omitted for list rows → the active filter's customer.
   * @ticket EB-4153
   */
  openCallDetails(
    row: CallInformation,
    initialTab?: CallDetailsTab,
    customerId?: string | null,
  ): void {
    // Prefer callNoteUserAccountId (which is consistently populated by the backend);
    // fall back to calledUserAccountId when the callnote UA is missing.
    const lookupUserAccountId = row.callNoteUserAccountId ?? row.calledUserAccountId;
    this.dialog.open(CallDetailsDialogComponent, {
      data: {
        cchsid: row.cchsid,
        callNoteId: row.callNoteId,
        // customerId stays invisible in the UI — feeds the jump-to-entity buttons
        // (Open phone service / Open user account / Open customer) inside the
        // Call Details tab. CallInformation rows don't carry the customer ID,
        // so we pull it from the active filter scope (which is always set
        // because the search form requires at least one of customer / UA / agent).
        // The URL path (EB-4153) passes the looked-up call's own customerId instead.
        customerId: customerId !== undefined ? customerId : this.activeFilters()?.customerId ?? null,
        lookupUserAccountId,
        initialTab,
        durations: {
          callDurationInSeconds: row.callDurationInSeconds,
          processDurationInSeconds: row.processDurationInSeconds,
          customerConnectSeconds: row.customerConnectSeconds,
          agentConnectSeconds: row.agentConnectSeconds,
          postProcessDurationInSeconds: row.postProcessDurationInSeconds,
        },
        summary: {
          timestamp: row.timestamp,
          callerNumber: row.callingNumber,
          calledNumber: row.callingFullNumber,
          accepted: row.accepted,
          status: row.status,
          agentName: row.agentName,
          type: row.type,
        },
      },
      width: '95vw',
      maxWidth: '1600px',
      maxHeight: '92vh',
      panelClass: 'cmt-dialog-panel',
      autoFocus: 'first-tabbable',
    });
  }

  // -------------------------------------------------------------------------
  // Column-def construction
  // -------------------------------------------------------------------------
  private buildColumnDefs(visible: Set<string>): ColDef<CallInformation>[] {
    const isVis = (f: keyof CallInformation): boolean =>
      DEFAULT_VISIBLE_FIELDS.includes(f) || visible.has(f as string);

    // Display order: identity (Timestamp, CCHSID) → outcome (Status, Ans., Type) →
    // owners (Knd A./CC) → numbers (Caller/Called) → Agent → time group (Ring×2, Talk,
    // Process, Wrap-up). The 5 duration columns share `cmt-time-header` / `cmt-time-cell`
    // so SCSS can mark them as a visual group; zero values get muted via cellClassRules.
    const timeColCommon: Partial<ColDef<CallInformation>> = {
      cellClass: 'font-mono-feat cmt-time-cell',
      cellClassRules: {
        // EB-3813: also mute negatives (e.g. postProcess<0 for vermittelt) —
        // formatter renders them as '—', the muted tone signals "not a value".
        'cmt-time-zero': (p) =>
          p.value == null || (typeof p.value === 'number' && p.value <= 0),
      },
      headerClass: 'cmt-time-header',
      valueFormatter: durationFormatter,
      // EB-3813: suppress raw-seconds tooltip for null/negative; otherwise users
      // see "-996 seconds" for vermittelt wrap-up which is misleading.
      tooltipValueGetter: (p) => {
        const v = p.value;
        if (v == null || typeof v !== 'number' || v < 0) return '';
        return `${v} seconds`;
      },
    };

    return [
      {
        field: 'timestamp',
        headerName: 'Timestamp',
        minWidth: 150,
        cellClass: 'font-mono-feat',
        tooltipField: 'timestamp',
        valueFormatter: berlinFormatter,
        // Newest first by default + visible sort indicator so users see the order.
        sort: 'desc',
        sortIndex: 0,
        hide: !isVis('timestamp'),
      },
      {
        field: 'cchsid',
        headerName: 'CCHSID',
        headerTooltip: 'Call session ID (Graylog correlation) — first 8 chars; click ⧉ to copy full',
        // Tight width: content is fixed (8 hex + ellipsis + 13px copy icon).
        // Capping max prevents sizeColumnsToFit() from inflating the column with
        // empty space when the viewport is wide.
        minWidth: 115,
        maxWidth: 125,
        cellRenderer: cchsidCellRenderer,
        hide: !isVis('cchsid'),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 110,
        maxWidth: 140,
        cellRenderer: statusCellRenderer,
        hide: !isVis('status'),
      },
      {
        field: 'accepted',
        headerName: 'Ans.',
        headerTooltip: 'Call Answered',
        minWidth: 80,
        maxWidth: 110,
        cellRenderer: acceptedCellRenderer,
        // AG-Grid cells are flex containers — `text-center` alone doesn't
        // center an `inline-flex` pill. `!justify-center` overrides the
        // default flex-start so the chip sits under the column header text.
        cellClass: '!justify-center',
        headerClass: '!justify-center',
        hide: !isVis('accepted'),
      },
      {
        field: 'type',
        headerName: 'Type',
        minWidth: 120,
        cellRenderer: badgeCellRenderer,
        hide: !isVis('type'),
      },
      {
        field: 'calledUserAccountId',
        headerName: 'Knd A.',
        headerTooltip: 'Customer (called UA) — Kunde des angerufenen User Accounts',
        minWidth: 90,
        cellClass: 'font-mono-feat',
        hide: !isVis('calledUserAccountId'),
      },
      {
        field: 'callNoteUserAccountId',
        headerName: 'Knd CC',
        headerTooltip: 'Customer (callnote UA) — Kunde des Callnote User Accounts',
        minWidth: 90,
        cellClass: 'font-mono-feat',
        hide: !isVis('callNoteUserAccountId'),
      },
      {
        field: 'callingNumber',
        headerName: 'Caller',
        headerTooltip: 'Calling number (old CMT: Anrufer)',
        minWidth: 150,
        cellClass: 'font-mono-feat',
        tooltipField: 'callingNumber',
        hide: !isVis('callingNumber'),
      },
      {
        field: 'callingFullNumber',
        headerName: 'Called Number',
        headerTooltip: 'Number that was called (old CMT: Angerufener)',
        minWidth: 150,
        cellClass: 'font-mono-feat',
        tooltipField: 'callingFullNumber',
        hide: !isVis('callingFullNumber'),
      },
      {
        field: 'agentName',
        headerName: 'Agent',
        minWidth: 160,
        tooltipField: 'agentName',
        hide: !isVis('agentName'),
      },
      // Time columns: data is always mm:ss (worst-case ~123:12, ~6 chars) — much
      // narrower than the headers. Tight min/max + reduced horizontal padding via
      // .cmt-time-cell/.cmt-time-header in SCSS keeps them compact even on wide
      // viewports where sizeColumnsToFit would otherwise inflate them.
      {
        ...timeColCommon,
        field: 'customerConnectSeconds',
        headerName: 'Ring (cust)',
        headerTooltip: 'Customer-side ring/connect time (old CMT: Klingelzeit)',
        wrapHeaderText: true,
        autoHeaderHeight: true,
        minWidth: 70,
        maxWidth: 90,
        hide: !isVis('customerConnectSeconds'),
      },
      {
        ...timeColCommon,
        field: 'agentConnectSeconds',
        headerName: 'Ring (agent)',
        headerTooltip: 'Agent-side ring/connect time',
        wrapHeaderText: true,
        autoHeaderHeight: true,
        minWidth: 70,
        maxWidth: 90,
        hide: !isVis('agentConnectSeconds'),
      },
      {
        ...timeColCommon,
        field: 'callDurationInSeconds',
        headerName: 'Call Duration',
        // EB-3813: renamed from "Talk" → "Call Duration" because for
        // transferred (vermittelt) calls this includes time AFTER transfer
        // (caller ↔ mediated party). Per-cell ↗ marker (talkCellRenderer)
        // highlights affected rows; the agent's actual talk time is in
        // "Agent Time" (formerly "Process").
        headerTooltip:
          'Total call duration from CALL to END_CALL (old CMT: Gesprächszeit). For transferred calls (↗) this includes time after transfer — for the agent talk time see "Agent Time".',
        cellRenderer: talkCellRenderer,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        minWidth: 80,
        maxWidth: 100,
        hide: !isVis('callDurationInSeconds'),
      },
      {
        ...timeColCommon,
        field: 'processDurationInSeconds',
        headerName: 'Agent Time',
        // EB-3813: renamed from "Process" — operators read this as the agent's
        // hands-on time per call (matches old CMT "Bearb.dauer").
        headerTooltip: 'Time the agent was actively handling the call (old CMT: Bearbeitungsdauer).',
        wrapHeaderText: true,
        autoHeaderHeight: true,
        minWidth: 80,
        maxWidth: 100,
        hide: !isVis('processDurationInSeconds'),
      },
      {
        ...timeColCommon,
        field: 'postProcessDurationInSeconds',
        headerName: 'Wrap-up',
        headerTooltip: 'Post-processing duration (old CMT: Nachbearbeitung)',
        minWidth: 85,
        maxWidth: 100,
        hide: !isVis('postProcessDurationInSeconds'),
      },
      // Toggleable / off-by-default columns below this line.
      {
        field: 'callerName',
        headerName: 'Caller Name',
        minWidth: 140,
        hide: !isVis('callerName'),
      },
      {
        field: 'callReason',
        headerName: 'Call Reason',
        minWidth: 140,
        tooltipField: 'callReason',
        hide: !isVis('callReason'),
      },
      {
        field: 'contactPerson',
        headerName: 'Contact Person',
        minWidth: 130,
        hide: !isVis('contactPerson'),
      },
      {
        field: 'brand',
        headerName: 'Brand',
        minWidth: 100,
        hide: !isVis('brand'),
      },
      {
        field: 'firstLoadBegin',
        headerName: 'First Load Begin',
        minWidth: 150,
        tooltipField: 'firstLoadBegin',
        valueFormatter: berlinFormatter,
        hide: !isVis('firstLoadBegin'),
      },
      {
        field: 'processEndTime',
        headerName: 'Process End',
        minWidth: 150,
        tooltipField: 'processEndTime',
        valueFormatter: berlinFormatter,
        hide: !isVis('processEndTime'),
      },
      {
        field: 'vrId',
        headerName: 'VR-ID',
        minWidth: 120,
        cellClass: 'font-mono-feat',
        hide: !isVis('vrId'),
      },
    ];
  }

  private loadInitialVisibleFields(): Set<string> {
    const prefs = this.callsService.loadColumnPrefs();
    return new Set(prefs.visibleFields);
  }
}
